/**
 * Dashboard — 운영 대시보드 (매출 우선 재설계)
 *
 * 기간 필터 + 밴드 0(알림) → A(매출) → B(성장) → C(마케팅) → D(추세 5종) → E(회원).
 * 매출은 /summary(netRevenue·paymentCount·newPayingCustomers), 추세는 /series.
 */

import { useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { DetailDrawer, type DrawerMetric } from './dashboard/DetailDrawer'
import { NotificationBanner } from './dashboard/NotificationBanner'
import { computeDelta } from './dashboard/metrics'
import {
  Card,
  Spinner,
  Alert,
  Sparkline,
  DateRangePicker,
  LineChart,
  CHART_COLORS,
  cn,
  formatNumber,
  type Series,
} from '@/ui'
import { resolveRange, SHARED_RANGE_PRESETS } from '@/lib/dateRange'
import styles from './Dashboard.module.css'

// 밴드 A 각 지표는 현재/이전 기간 쌍.
interface MetricPair {
  current: number
  previous: number
}

// 광고 효율 요약 — /ads 응답의 summary 일부만 사용(상세는 전용 페이지 /commerce/ads).
interface AdsData {
  summary: {
    spend: MetricPair
    roas: { current: number | null; previous: number | null }
    cac: { current: number | null; previous: number | null }
  }
  channels: { channel: string; conversionValue: number }[]
}

interface SummaryData {
  // 매출 (밴드 A) — 신규 필드라 API 배포 순서/스키마 드리프트로 누락될 수 있어 옵셔널로 방어.
  netRevenue?: MetricPair
  paymentCount?: MetricPair
  newPayingCustomers?: MetricPair
  // 성장 (밴드 B)
  newUsers: MetricPair
  smartstoreRegistrations: MetricPair
  aiGenerations: MetricPair
  aiCost: MetricPair // 서버 유지, 프론트 미사용
  publishedInvitations: MetricPair
  newInquiries: MetricPair
  // 헬스 (밴드 0 배너 소스)
  openInquiries: number
  aiFailRate24h: number // 0–100 float
  expiringSoon: number
  inactiveUserInvitations: number
  activeInvitations: number
  // 기간 메타
  from: string
  to: string
}

interface UserSegmentsData {
  totalUsers: number
  usersWithInvitation: number
  usersWithThankyou: number
  usersWithAiGeneration: number
  usersWithNothing: number
}

interface PendingRefundsData {
  count: number
}

// 밴드 D 추세 차트 — 일자별 시계열 행.
interface SeriesRow {
  date: string
  newUsers: number
  smartstoreRegistrations: number
  aiGenerations: number
  aiFailures: number
  rsvps: number
  guestbookEntries: number
  publishedInvitations: number
  newInquiries: number
  netRevenue: number
  paymentCount: number
}

interface SeriesData {
  from: string
  to: string
  rows: SeriesRow[]
}

// 추세 차트에 쓰는 숫자 메트릭 키만 추림 (date 제외).
type SeriesMetricKey = Exclude<keyof SeriesRow, 'date'>

/** rows 에서 단일 메트릭을 LineChart Series 로 변환. */
function toSeries(
  rows: SeriesRow[],
  key: SeriesMetricKey,
  label: string,
  color: string,
): Series {
  return {
    key,
    label,
    color,
    data: rows.map((r) => ({ date: r.date, value: r[key] })),
  }
}

// 빠른검색 프리셋 — admin 공용(SHARED_RANGE_PRESETS). 기간 해석은 resolveRange(공용) 사용.
type PresetKey = string


interface KpiTileProps {
  label: string
  /** 표시 값 (숫자 또는 '12.3%' 같이 가공된 문자열) */
  value: string
  /** 증감 표시 — null 이면 표시 안 함 */
  delta: { text: string; up: boolean } | null
  /** Task 3.2 에서 채울 스파크라인 데이터 슬롯 */
  spark?: number[]
  /** 클릭 시 상세 Drawer 열기 — 없으면 비클릭 타일 */
  onClick?: () => void
  /** 로딩 중이면 클릭 비활성 (값은 갱신 전까지 기존값 유지 — 스켈레톤 미사용) */
  loading?: boolean
}

function KpiTile({ label, value, delta, spark, onClick, loading }: KpiTileProps) {
  return (
    <Card compact className={cn(styles.tile, !loading && onClick && styles.clickable)} onClick={loading ? undefined : onClick}>
      <p className={styles.tileLabel}>{label}</p>
      <p className={styles.tileValue}>{value}</p>
      {delta && (
        <p className={cn(styles.tileDelta, delta.up ? styles.deltaUp : styles.deltaDown)}>
          {delta.text}
        </p>
      )}
      {spark && spark.length > 1 && (
        <div className={styles.tileSpark}>
          <Sparkline data={spark} />
        </div>
      )}
    </Card>
  )
}

type StatusTone = 'neutral' | 'warning' | 'danger'

interface StatusTileProps {
  label: string
  value: string
  tone: StatusTone
  /** 클릭 시 상세 Drawer 열기 — 없으면 비클릭 타일 */
  onClick?: () => void
  /** 로딩 중이면 클릭 비활성 (스켈레톤 미사용) */
  loading?: boolean
}

function StatusTile({ label, value, tone, onClick, loading }: StatusTileProps) {
  return (
    <Card
      compact
      className={cn(
        styles.statusTile,
        !loading && tone === 'warning' && styles.toneWarning,
        !loading && tone === 'danger' && styles.toneDanger,
        !loading && onClick && styles.clickable,
      )}
      onClick={loading ? undefined : onClick}
    >
      <p className={styles.tileLabel}>{label}</p>
      <p className={styles.statusValue}>{value}</p>
    </Card>
  )
}

export function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { from, to, preset } = useMemo(() => resolveRange(searchParams), [searchParams])

  const { data: summary, error } = useApiFetch<SummaryData>(
    `/api/admin/dashboard/summary?from=${from}&to=${to}`,
  )
  // 환불 요청 건수는 별도 fetch — 느리거나 실패해도 대시보드 본문에 영향 없음.
  const { data: refunds } = useApiFetch<PendingRefundsData>('/api/admin/dashboard/pending-refunds')
  // 밴드 D 추세 차트 + 밴드 A 스파크라인용 시계열.
  const { data: series } = useApiFetch<SeriesData>(
    `/api/admin/dashboard/series?from=${from}&to=${to}`,
  )
  // 광고 효율(ROAS/CAC) — 별도 fetch (광고비 미적재 시 0/'-' 로 표시).
  const { data: ads } = useApiFetch<AdsData>(
    `/api/admin/dashboard/ads?from=${from}&to=${to}`,
  )
  // 밴드 E — 회원 행동 분류 스냅샷 (기간 무관).
  const { data: userSegments } = useApiFetch<UserSegmentsData>('/api/admin/dashboard/user-segments')

  // 타일 클릭 시 열리는 상세 Drawer 의 대상 metric.
  const [drawerMetric, setDrawerMetric] = useState<DrawerMetric | null>(null)

  // DateRangePicker onChange — 프리셋이면 preset 키를, 커스텀 범위면 from/to 를 URL 에 커밋.
  const handleRangeChange = (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    if (meta.presetKey) {
      setSearchParams({ preset: meta.presetKey })
    } else if (range.from && range.to) {
      setSearchParams({ from: range.from, to: range.to })
    }
  }

  // 타일 클릭 비활성 판정 (초기 로딩 — 값 도착 전).
  const tilesLoading = !summary && !error
  // 우측 상단 스피너: 초기 로딩 + 기간 변경 갱신 모두 표시.
  // useApiFetch 는 SWR 패턴이라 refetch 중 isLoading 을 안 올리므로,
  // 요청한 from/to 와 응답 summary.from/to 가 다르면 '갱신 중'으로 본다.
  const refreshing = !error && (!summary || summary.from !== from || summary.to !== to)

  return (
    <div className={styles.root}>
      <FilterBar
        value={{ from, to }}
        activePresetKey={preset}
        onRangeChange={handleRangeChange}
        loading={refreshing}
      />

      {/* 밴드 0 — 오늘 할 일 알림 배너. 조건 충족 항목 있을 때만 렌더. */}
      {summary && (
        <NotificationBanner
          input={{
            openInquiries: summary.openInquiries,
            pendingRefunds: refunds ? refunds.count : null,
            aiFailRate24h: summary.aiFailRate24h,
            expiringSoon: summary.expiringSoon,
          }}
          onChipClick={(m) => setDrawerMetric(m)}
        />
      )}

      {error && <Alert variant="danger">불러오지 못했습니다: {error}</Alert>}

      {!error && (
        <>
          {/* 밴드 A — 매출·결제 (헤드라인 4칸) */}
          <section className={styles.bandA}>
            <KpiTile
              label="순매출"
              value={summary?.netRevenue ? `${formatNumber(summary.netRevenue.current)}원` : ''}
              delta={summary?.netRevenue ? computeDelta(summary.netRevenue.current, summary.netRevenue.previous) : null}
              spark={series?.rows.map((r) => r.netRevenue)}
              loading={tilesLoading}
            />
            <KpiTile
              label="결제 건수"
              value={summary?.paymentCount ? formatNumber(summary.paymentCount.current) : ''}
              delta={summary?.paymentCount ? computeDelta(summary.paymentCount.current, summary.paymentCount.previous) : null}
              spark={series?.rows.map((r) => r.paymentCount)}
              loading={tilesLoading}
            />
            <KpiTile
              label="신규 결제 고객"
              value={summary?.newPayingCustomers ? formatNumber(summary.newPayingCustomers.current) : ''}
              delta={summary?.newPayingCustomers ? computeDelta(summary.newPayingCustomers.current, summary.newPayingCustomers.previous) : null}
              loading={tilesLoading}
            />
            {summary?.newPayingCustomers ? (
              <KpiTile {...paymentConversionTile(summary)} />
            ) : (
              <KpiTile label="결제 전환율" value="" delta={null} loading={tilesLoading} />
            )}
          </section>

          {/* 밴드 B — 성장·활성 5칸 */}
          <section className={styles.bandB}>
            <KpiTile
              label="신규 가입자"
              value={summary ? formatNumber(summary.newUsers.current) : ''}
              delta={summary ? computeDelta(summary.newUsers.current, summary.newUsers.previous) : null}
              spark={series?.rows.map((r) => r.newUsers)}
              onClick={() => setDrawerMetric('newUsers')}
              loading={tilesLoading}
            />
            <KpiTile
              label="주문번호 등록"
              value={summary ? formatNumber(summary.smartstoreRegistrations.current) : ''}
              delta={summary ? computeDelta(summary.smartstoreRegistrations.current, summary.smartstoreRegistrations.previous) : null}
              spark={series?.rows.map((r) => r.smartstoreRegistrations)}
              onClick={() => setDrawerMetric('smartstoreRegistrations')}
              loading={tilesLoading}
            />
            {summary ? (
              <KpiTile {...conversionTile(summary)} />
            ) : (
              <KpiTile label="가입→등록 전환" value="" delta={null} loading={tilesLoading} />
            )}
            <KpiTile
              label="발행 청첩장"
              value={summary ? formatNumber(summary.publishedInvitations.current) : ''}
              delta={summary ? computeDelta(summary.publishedInvitations.current, summary.publishedInvitations.previous) : null}
              spark={series?.rows.map((r) => r.publishedInvitations)}
              onClick={() => setDrawerMetric('publishedInvitations')}
              loading={tilesLoading}
            />
            <KpiTile
              label="AI 사진 생성"
              value={summary ? formatNumber(summary.aiGenerations.current) : ''}
              delta={summary ? computeDelta(summary.aiGenerations.current, summary.aiGenerations.previous) : null}
              spark={series?.rows.map((r) => r.aiGenerations)}
              onClick={() => setDrawerMetric('aiGenerations')}
              loading={tilesLoading}
            />
          </section>

          {/* 밴드 C — 마케팅 요약 4칸 (클릭 → /commerce/ads) */}
          <section className={styles.bandMarketing}>
            <KpiTile
              label="광고비"
              value={ads ? `${formatNumber(ads.summary.spend.current)}원` : ''}
              delta={ads ? computeDelta(ads.summary.spend.current, ads.summary.spend.previous) : null}
              loading={!ads}
              onClick={() => navigate('/commerce/ads')}
            />
            <KpiTile
              label="ROAS(실결제)"
              value={ads ? (ads.summary.roas.current != null ? `${ads.summary.roas.current.toFixed(1)}x` : '-') : ''}
              delta={
                ads && ads.summary.roas.current != null && ads.summary.roas.previous != null
                  ? computeDelta(ads.summary.roas.current, ads.summary.roas.previous)
                  : null
              }
              loading={!ads}
              onClick={() => navigate('/commerce/ads')}
            />
            <KpiTile
              label="CAC"
              value={ads ? (ads.summary.cac.current != null ? `${formatNumber(ads.summary.cac.current)}원` : '-') : ''}
              delta={
                ads && ads.summary.cac.current != null && ads.summary.cac.previous != null
                  ? computeDelta(ads.summary.cac.current, ads.summary.cac.previous)
                  : null
              }
              loading={!ads}
              onClick={() => navigate('/commerce/ads')}
            />
            <KpiTile
              label="전환매출(광고 리포트)"
              value={ads ? `${formatNumber(sumConversionValue(ads))}원` : ''}
              delta={null}
              loading={!ads}
              onClick={() => navigate('/commerce/ads')}
            />
          </section>
        </>
      )}

      {/* 밴드 D — 추세 차트 5종 (series 자체 로딩) */}
      <section className={styles.bandC}>
        <Card compact className={styles.chartCard}>
          <p className={styles.chartTitle}>일별 순매출</p>
          <div className={styles.chartBody}>
            {series ? (
              <LineChart
                series={[
                  toSeries(series.rows, 'netRevenue', '순매출', CHART_COLORS.primary),
                ]}
                showTooltip
                showLegend
              />
            ) : (
              <div className={styles.center}>
                <Spinner />
              </div>
            )}
          </div>
        </Card>

        <Card compact className={styles.chartCard}>
          <p className={styles.chartTitle}>일별 결제 건수</p>
          <div className={styles.chartBody}>
            {series ? (
              <LineChart
                series={[
                  toSeries(series.rows, 'paymentCount', '결제 건수', CHART_COLORS.info),
                ]}
                showTooltip
                showLegend
              />
            ) : (
              <div className={styles.center}>
                <Spinner />
              </div>
            )}
          </div>
        </Card>

            <Card compact className={styles.chartCard}>
              <p className={styles.chartTitle}>가입 vs 주문번호 등록</p>
              <div className={styles.chartBody}>
                {series ? (
                  <LineChart
                    series={[
                      toSeries(series.rows, 'newUsers', '신규 가입', CHART_COLORS.info),
                      toSeries(
                        series.rows,
                        'smartstoreRegistrations',
                        '주문번호 등록',
                        CHART_COLORS.primary,
                      ),
                    ]}
                    showTooltip
                    showLegend
                  />
                ) : (
                  <div className={styles.center}>
                    <Spinner />
                  </div>
                )}
              </div>
            </Card>

            <Card compact className={styles.chartCard}>
              <p className={styles.chartTitle}>AI 생성 · 실패</p>
              <div className={styles.chartBody}>
                {series ? (
                  <LineChart
                    series={[
                      toSeries(series.rows, 'aiGenerations', 'AI 생성', CHART_COLORS.info),
                      toSeries(series.rows, 'aiFailures', '실패', CHART_COLORS.danger),
                    ]}
                    showTooltip
                    showLegend
                  />
                ) : (
                  <div className={styles.center}>
                    <Spinner />
                  </div>
                )}
              </div>
            </Card>

            <Card compact className={styles.chartCard}>
              <p className={styles.chartTitle}>RSVP · 방명록</p>
              <div className={styles.chartBody}>
                {series ? (
                  <LineChart
                    series={[
                      toSeries(series.rows, 'rsvps', 'RSVP', CHART_COLORS.primary),
                      toSeries(series.rows, 'guestbookEntries', '방명록', CHART_COLORS.success),
                    ]}
                    showTooltip
                    showLegend
                  />
                ) : (
                  <div className={styles.center}>
                    <Spinner />
                  </div>
                )}
              </div>
            </Card>
      </section>

      {/* 밴드 E — 회원 행동 분류 (스냅샷, 기간 필터 무관) */}
      <section className={styles.bandD}>
        <p className={styles.bandDTitle}>회원 행동 분류 (전체 누적)</p>
        <div className={styles.bandDGrid}>
          <StatusTile
            label="청첩장 만든 회원"
            value={userSegments ? formatNumber(userSegments.usersWithInvitation) : '—'}
            tone="neutral"
          />
          <StatusTile
            label="감사장 만든 회원"
            value={userSegments ? formatNumber(userSegments.usersWithThankyou) : '—'}
            tone="neutral"
          />
          <StatusTile
            label="AI 생성 사용 회원"
            value={userSegments ? formatNumber(userSegments.usersWithAiGeneration) : '—'}
            tone="neutral"
          />
          <StatusTile
            label="아무것도 안 만든 회원"
            value={userSegments ? formatNumber(userSegments.usersWithNothing) : '—'}
            tone={userSegments && userSegments.usersWithNothing > 0 ? 'warning' : 'neutral'}
          />
        </div>
      </section>

      <DetailDrawer
        metric={drawerMetric}
        from={from}
        to={to}
        onClose={() => setDrawerMetric(null)}
      />
    </div>
  )
}

/** 밴드 C 전환매출(참고) — /ads channels 의 conversionValue 합. */
function sumConversionValue(ads: AdsData): number {
  return ads.channels.reduce((sum, ch) => sum + ch.conversionValue, 0)
}

/** 결제 전환율 타일 — 전환율(%) = 신규결제고객/신규가입자*100, 델타는 이전 기간 대비 %p. */
function paymentConversionTile(summary: SummaryData): KpiTileProps {
  // newPayingCustomers 는 옵셔널 필드(스키마 드리프트 방어) — 없으면 0으로 취급.
  const payingCur = summary.newPayingCustomers?.current ?? 0
  const payingPrev = summary.newPayingCustomers?.previous ?? 0

  const curRatio = summary.newUsers.current > 0 ? (payingCur / summary.newUsers.current) * 100 : 0
  const prevRatio = summary.newUsers.previous > 0 ? (payingPrev / summary.newUsers.previous) * 100 : 0

  let delta: KpiTileProps['delta'] = null
  if (summary.newUsers.previous > 0) {
    const diff = curRatio - prevRatio // %p
    const up = diff >= 0
    delta = { text: `${up ? '▲' : '▼'} ${Math.abs(diff).toFixed(1)}%p`, up }
  }

  return {
    label: '결제 전환율',
    value: `${curRatio.toFixed(1)}%`,
    delta,
  }
}

/** 가입→등록 전환 타일 — 전환율(%) = 등록/가입*100, 델타는 이전 기간 비율 대비 %p. */
function conversionTile(summary: SummaryData): KpiTileProps {
  const curRatio =
    summary.newUsers.current > 0
      ? (summary.smartstoreRegistrations.current / summary.newUsers.current) * 100
      : 0
  const prevRatio =
    summary.newUsers.previous > 0
      ? (summary.smartstoreRegistrations.previous / summary.newUsers.previous) * 100
      : 0

  let delta: KpiTileProps['delta'] = null
  if (summary.newUsers.previous > 0) {
    const diff = curRatio - prevRatio // %p
    const up = diff >= 0
    delta = { text: `${up ? '▲' : '▼'} ${Math.abs(diff).toFixed(1)}%p`, up }
  }

  return {
    label: '가입→등록 전환',
    value: `${curRatio.toFixed(1)}%`,
    delta,
  }
}

interface FilterBarProps {
  value: { from: string; to: string }
  activePresetKey: PresetKey | null
  onRangeChange: (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => void
  /** 데이터 갱신 중이면 우측 상단 스피너 표시 */
  loading?: boolean
}

function FilterBar({ value, activePresetKey, onRangeChange, loading }: FilterBarProps) {
  return (
    <div className={styles.filterBar}>
      <div className={styles.customRange}>
        <DateRangePicker
          size="sm"
          value={value}
          activePresetKey={activePresetKey}
          presets={SHARED_RANGE_PRESETS}
          onChange={onRangeChange}
        />
        {loading && <Spinner size="sm" className={styles.refreshSpinner} />}
      </div>
    </div>
  )
}
